/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */

import * as React from 'react';
import { css, StyleSheet } from 'aphrodite';
import { BrandColors, LinkButton, Text, MaterialIcon, Tooltip } from '@blend/enterprise-components';
import { cardWidth } from 'blb-styles/constants';
import {
  generateAppointmentMethodString,
  generateNotaryName,
  generateBorrowerPreferredLocationString,
  generatePreferredLocationString,
  generateTimePreferenceString,
  getRonTransactionPlatformName,
  getTimezone,
  locationLexeme,
  getScheduledDateTimeText,
} from './utils';
import { convertToDateFormatUTC, convertToISOString } from 'blb-utils/date';
import { capitalize, compact } from 'lodash';

namespace ScheduledClosingAppointmentSection {
  export interface Props extends React.HTMLAttributes<HTMLDivElement> {
    closing: EClose.FullClosing;
  }
}

const styles = StyleSheet.create({
  borrowerPreferences: {
    background: BrandColors.ACCENT_YELLOW_07,
    borderLeft: `4px solid ${BrandColors.ACCENT_YELLOW}`,
    paddingLeft: '8px',
  },
  container: {
    alignItems: 'center',
    background: BrandColors.WHITE,
    border: `1px solid ${BrandColors.CLOUD_50}`,
    borderRadius: 6,
    flexBasis: cardWidth,
    maxWidth: cardWidth,
    padding: '12px 24px',
  },
  multicardContainer: {
    alignItems: 'center',
    background: BrandColors.WHITE,
    border: `1px solid ${BrandColors.CLOUD_50}`,
    borderRadius: 6,
    flexBasis: cardWidth,
    maxWidth: cardWidth,
    padding: '12px 24px',
    margin: '10px 0px',
  },
  grayBorrowerPreferences: {
    background: BrandColors.CLOUD_10,
    borderLeft: `4px solid ${BrandColors.NIGHT_40}`,
    marginTop: '4px',
    padding: '4px 0 4px 4px',
  },
  section: {
    ':not(:last-child)': {
      marginBottom: '12px',
    },
    lineHeight: '4px',
  },
  sectionTitle: {
    marginBottom: '4px',
    textTransform: 'uppercase',
    display: 'flex',
  },
  tooltipIcon: {
    marginLeft: 5,
  },
});

export const UnscheduledClosingAppointmentSection: React.FC<ScheduledClosingAppointmentSection.Props> = ({
  closing,
}) => {
  const borrowerClosingMethod = closing.schedule!.closingMethod!;
  const borrowerPreferredTimeSlots = generateTimePreferenceString(closing.schedule!.preferredTimes!);
  const borrowerPreferredLocation = generateBorrowerPreferredLocationString(closing.schedule!.preferredLocation!);
  const borrowerPreferredAppointmentMethod = generateAppointmentMethodString(borrowerClosingMethod);
  const timezone = getTimezone(closing.schedule!.timezone!);
  const isRon = closing.closingType === 'RON';

  return (
    <div className={css(styles.container)}>
      <div className={css(styles.section)}>
        <div className={css(styles.sectionTitle)}>
          <Text color={BrandColors.NIGHT_80} fontSize="md" weight="semibold">
            Date and Time
          </Text>
        </div>
        <div className={css(styles.borrowerPreferences)}>
          <Text fontSize="md">
            Borrower prefers {convertToDateFormatUTC(closing.scheduledCeremonyTime ?? closing.closingStart!)} from{' '}
            {borrowerPreferredTimeSlots} {timezone}
          </Text>
        </div>
      </div>
      {!isRon && (
        <div className={css(styles.section)}>
          <div className={css(styles.sectionTitle)}>
            <Text color={BrandColors.NIGHT_80} fontSize="md" weight="semibold">
              Closing Method and Location
            </Text>
          </div>
          <div className={css(styles.borrowerPreferences)}>
            <Text fontSize="md">
              Borrower prefers {borrowerPreferredAppointmentMethod} – meet {locationLexeme(borrowerClosingMethod)}{' '}
              {borrowerPreferredLocation}
            </Text>
          </div>
        </div>
      )}
      {isRon && (
        <div className={css(styles.section)}>
          <div className={css(styles.sectionTitle)}>
            <Text color={BrandColors.NIGHT_80} fontSize="md" weight="semibold">
              Remote Online Notarization Platform
            </Text>
          </div>
          <Text fontSize="md">{getRonTransactionPlatformName(closing)}</Text>
        </div>
      )}
    </div>
  );
};

export const ScheduledClosingAppointmentSection: React.FC<ScheduledClosingAppointmentSection.Props> = ({ closing }) => {
  const borrowerClosingMethod = closing.schedule!.closingMethod!;
  const borrowerPreferredTimeSlots = generateTimePreferenceString(closing.schedule!.preferredTimes!);
  const borrowerPreferredLocation = generateBorrowerPreferredLocationString(closing.schedule!.preferredLocation!);
  const borrowerPreferredAppointmentMethod = generateAppointmentMethodString(borrowerClosingMethod);
  const chosenAppointmentMethod = generateAppointmentMethodString(closing.schedule!.chosenClosingMethod!);
  const chosenLocation = generatePreferredLocationString(closing.schedule!.chosenLocation);
  const timezone = getTimezone(closing.schedule!.timezone!);
  const notaryName = generateNotaryName(closing.schedule!);
  const isRon = closing.closingType === 'RON';
  const [showBorrowerPreferences, setShowBorrowerPreferences] = React.useState(false);
  return (
    <div className={css(styles.container)}>
      <div className={css(styles.section)}>
        <div className={css(styles.sectionTitle)}>
          <Text color={BrandColors.NIGHT_80} fontSize="md" weight="semibold">
            Date and Time
          </Text>
        </div>
        <div>
          <Text fontSize="md">{getScheduledDateTimeText(closing.schedule!)}</Text>
        </div>
        {showBorrowerPreferences && (
          <div className={css(styles.grayBorrowerPreferences)}>
            <Text fontSize="md">
              Borrower prefers {convertToDateFormatUTC(closing.scheduledCeremonyTime ?? closing.closingStart!)} from{' '}
              {borrowerPreferredTimeSlots} {timezone}
            </Text>
          </div>
        )}
      </div>
      {!isRon && (
        <div className={css(styles.section)}>
          <div className={css(styles.sectionTitle)}>
            <Text color={BrandColors.NIGHT_80} fontSize="md" weight="semibold">
              Closing Method and Location
            </Text>
          </div>
          <div>
            <Text fontSize="md">
              {capitalize(chosenAppointmentMethod)} – {chosenLocation}
            </Text>
          </div>
          {showBorrowerPreferences && (
            <div className={css(styles.grayBorrowerPreferences)}>
              <Text fontSize="md">
                Borrower prefers {borrowerPreferredAppointmentMethod} – meet {locationLexeme(borrowerClosingMethod)}{' '}
                {borrowerPreferredLocation}
              </Text>
            </div>
          )}
        </div>
      )}
      {isRon && (
        <div className={css(styles.section)}>
          <div className={css(styles.sectionTitle)}>
            <Text color={BrandColors.NIGHT_80} fontSize="md" weight="semibold">
              Remote Online Notarization Platform
            </Text>
          </div>
          <Text fontSize="md">{getRonTransactionPlatformName(closing)}</Text>
        </div>
      )}
      <div className={css(styles.section)}>
        <div className={css(styles.sectionTitle)}>
          <Text color={BrandColors.NIGHT_80} fontSize="md" weight="semibold">
            Notary
          </Text>
        </div>
        <div>
          <Text fontSize="md">{notaryName}</Text>
        </div>
      </div>
      <LinkButton
        label={showBorrowerPreferences ? 'Hide borrower preferences' : 'Show borrower preferences'}
        onClick={() => setShowBorrowerPreferences(!showBorrowerPreferences)}
      />
    </div>
  );
};

namespace ScheduledV2ClosingAppointmentSection {
  export interface Props extends React.HTMLAttributes<HTMLDivElement> {
    closing: EClose.FullClosing;
    schedule: EClose.API.ScheduleResponse;
  }
}

const dateTimeSectionTitleV2 = (
  <div className={css(styles.sectionTitle)}>
    <Text color={BrandColors.NIGHT_80} fontSize="md" weight="semibold">
      Date and Time
    </Text>
    <Tooltip position="right" text="Displayed time slots are in the order of borrower's preferences.">
      <div className={css(styles.tooltipIcon)}>
        <MaterialIcon color={BrandColors.SKY_TONE_30} size={16} type="info" />
      </div>
    </Tooltip>
  </div>
);

export const UnscheduledV2ClosingAppointmentSection: React.FC<ScheduledV2ClosingAppointmentSection.Props> = ({
  closing,
  schedule,
}) => {
  const borrowerClosingMethod = schedule.closingMethod!;
  const borrowerPreferredTimeSlots = generateTimePreferenceString(schedule.preferredTimes!);
  const borrowerPreferredLocation = generateBorrowerPreferredLocationString(schedule.preferredLocation!);
  const borrowerPreferredAppointmentMethod = generateAppointmentMethodString(borrowerClosingMethod);
  const timezone = getTimezone(schedule.timezone!);
  const isRon = closing.closingType === 'RON';
  const borrowerName = schedule.borrowerName;

  return (
    <div className={css(styles.multicardContainer)}>
      <div className={css(styles.section)}>
        <div className={css(styles.sectionTitle)}>
          <Text color={BrandColors.NIGHT_80} fontSize="md" weight="semibold">
            Borrower
          </Text>
        </div>
        <Text fontSize="md">{borrowerName}</Text>
      </div>
      <div className={css(styles.section)}>
        {dateTimeSectionTitleV2}
        <div className={css(styles.borrowerPreferences)}>
          <Text fontSize="md">
            Borrower prefers {convertToDateFormatUTC(closing.scheduledCeremonyTime ?? closing.closingStart!)} from{' '}
            {borrowerPreferredTimeSlots} {timezone}
          </Text>
        </div>
      </div>
      {!isRon && (
        <div className={css(styles.section)}>
          <div className={css(styles.sectionTitle)}>
            <Text color={BrandColors.NIGHT_80} fontSize="md" weight="semibold">
              Closing Method and Location
            </Text>
          </div>
          <div className={css(styles.borrowerPreferences)}>
            <Text fontSize="md">
              Borrower prefers {borrowerPreferredAppointmentMethod} – meet {locationLexeme(borrowerClosingMethod)}{' '}
              {borrowerPreferredLocation}
            </Text>
          </div>
        </div>
      )}
      {isRon && generateRONPlatform(closing)}
    </div>
  );
};

export const ScheduledV2ClosingAppointmentSection: React.FC<ScheduledV2ClosingAppointmentSection.Props> = ({
  closing,
  schedule,
}) => {
  const borrowerClosingMethod = schedule.closingMethod!;
  const borrowerPreferredTimeSlots = generateTimePreferenceString(schedule.preferredTimes!);
  const borrowerPreferredLocation = generateBorrowerPreferredLocationString(schedule.preferredLocation!);
  const borrowerPreferredAppointmentMethod = generateAppointmentMethodString(borrowerClosingMethod);
  const chosenAppointmentMethod = generateAppointmentMethodString(schedule.chosenClosingMethod!);
  const chosenLocation = generatePreferredLocationString(schedule.chosenLocation);
  const timezone = getTimezone(schedule.timezone!);
  const notaryName = generateNotaryName(schedule);
  const isRon = closing.closingType === 'RON';
  const [showBorrowerPreferences, setShowBorrowerPreferences] = React.useState(false);
  const borrowerName = schedule.borrowerName;
  const scheduleDateAsISO = convertToISOString(schedule.scheduleDateTime);
  const scheduleDate = scheduleDateAsISO ? convertToDateFormatUTC(scheduleDateAsISO) : null;
  const closingMethodAndLocation = compact([capitalize(chosenAppointmentMethod), chosenLocation]).join(' - ');

  return (
    <div className={css(styles.multicardContainer)}>
      <div className={css(styles.section)}>
        <div className={css(styles.sectionTitle)}>
          <Text color={BrandColors.NIGHT_80} fontSize="md" weight="semibold">
            Borrower
          </Text>
        </div>
        <Text fontSize="md">{borrowerName}</Text>
      </div>
      <div className={css(styles.section)}>
        {dateTimeSectionTitleV2}
        <div>
          <Text fontSize="md">{getScheduledDateTimeText(closing.schedule!)}</Text>
        </div>
        {showBorrowerPreferences && (
          <div className={css(styles.grayBorrowerPreferences)}>
            <Text fontSize="md">
              Borrower prefers {scheduleDate} from {borrowerPreferredTimeSlots} {timezone}
            </Text>
          </div>
        )}
      </div>
      {!isRon && (
        <div className={css(styles.section)}>
          <div className={css(styles.sectionTitle)}>
            <Text color={BrandColors.NIGHT_80} fontSize="md" weight="semibold">
              Closing Method and Location
            </Text>
          </div>
          <div>
            <Text fontSize="md">{closingMethodAndLocation}</Text>
          </div>
          {showBorrowerPreferences && (
            <div className={css(styles.grayBorrowerPreferences)}>
              <Text fontSize="md">
                Borrower prefers {borrowerPreferredAppointmentMethod} – meet {locationLexeme(borrowerClosingMethod)}{' '}
                {borrowerPreferredLocation}
              </Text>
            </div>
          )}
        </div>
      )}
      {isRon && generateRONPlatform(closing)}
      {notaryName && (
        <div className={css(styles.section)}>
          <div className={css(styles.sectionTitle)}>
            <Text color={BrandColors.NIGHT_80} fontSize="md" weight="semibold">
              Notary
            </Text>
          </div>
          <div>
            <Text fontSize="md">{notaryName}</Text>
          </div>
        </div>
      )}
      <LinkButton
        label={showBorrowerPreferences ? 'Hide borrower preferences' : 'Show borrower preferences'}
        onClick={() => setShowBorrowerPreferences(!showBorrowerPreferences)}
      />
    </div>
  );
};

const generateRONPlatform = (closing: EClose.FullClosing): React.ReactElement => {
  return (
    <div className={css(styles.section)}>
      <div className={css(styles.sectionTitle)}>
        <Text color={BrandColors.NIGHT_80} fontSize="md" weight="semibold">
          Remote Online Notarization Platform
        </Text>
      </div>
      <Text fontSize="md">{getRonTransactionPlatformName(closing)}</Text>
    </div>
  );
};
